import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getRandomInt } from 'app/utils/getters';
import { Trigger } from '../hint.types';
import { HintContext } from '../hint.context';
import { HintProviderContext } from './hint-provider';
import { clearTimer } from '../hint.helpers';

export type RootProps = {
  delayDuration?: number;
  triggerMode?: Trigger;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const Root = ({
  children,
  delayDuration = 300,
  triggerMode = 'both',
  open: controlledOpen,
  onOpenChange,
}: PropsWithChildren<RootProps>) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const open = isControlled ? controlledOpen! : internalOpen;

  const { isAnyOpen, notifyOpen, notifyClose } =
    useContext(HintProviderContext);

  const triggerRef = useRef<HTMLElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const contentId = useMemo(
    () => String(getRandomInt(0, Number.MAX_SAFE_INTEGER)),
    []
  );

  const wasOpen = useRef<boolean>(false);

  const commit = useCallback(
    (next: boolean) => {
      if (next && !wasOpen.current) {
        notifyOpen();
      }

      if (!next && wasOpen.current) {
        notifyClose();
      }
      wasOpen.current = next;

      if (isControlled) {
        onOpenChange?.(next);
      } else {
        setInternalOpen(next);
      }
    },
    [isControlled, onOpenChange, notifyOpen, notifyClose]
  );

  const closeImmediate = useCallback(() => {
    clearTimer(openTimer);
    clearTimer(closeTimer);
    commit(false);
  }, [commit]);

  const setOpen = useCallback(
    (value: boolean, source: 'hover' | 'click') => {
      if (source === 'click') {
        clearTimer(openTimer);
        clearTimer(closeTimer);
        commit(!open);

        return;
      }

      if (value) {
        clearTimer(closeTimer);
        const delay = isAnyOpen ? 0 : delayDuration;
        openTimer.current = setTimeout(() => commit(true), delay);
      } else {
        clearTimer(openTimer);
        closeTimer.current = setTimeout(() => commit(false), 0);
      }
    },
    [open, isAnyOpen, delayDuration, commit]
  );

  useEffect(
    () => () => {
      clearTimer(openTimer);
      clearTimer(closeTimer);

      if (wasOpen.current) {
        notifyClose();
      }
    },
    [notifyClose]
  );

  return (
    <HintContext.Provider
      value={{
        open,
        setOpen,
        triggerRef,
        contentId,
        triggerMode,
        closeImmediate,
      }}
    >
      {children}
    </HintContext.Provider>
  );
};
